/* eslint-disable react/prop-types */
import {
  useState,
  useRef,
  forwardRef,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useImperativeHandle,
  type MutableRefObject,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../core';
import { FAIcon, IconStyle } from '../../Icons/Icons';
import { MLPVersion, Theme } from '../../types/enums';
import DropDown, { DropDownType } from '../../DropDown/DropDown';
import { type Data } from '../../DropDown/types/propsType';
import { useTranslation } from '../../Translation/TranslationProvider';
import { DatePickerType, TimePicker } from '../DatePicker';
import { CalendarType, CalendarYearDropdownType, SelectorType, TimePickerModal } from '../types/enums';
import RawModal, { TextAlign } from '../../Modal/RawModal';
import modalStyles from '../../Modal/Modal.module.scss';
import Button, { ButtonSize, ButtonStyles, ButtonTypes } from '../../Button/Button';
import {
  getTimeList,
  newYearDirection,
  newMonthDirection,
  selectedRangeLabel,
  calendarDaysMapping,
  getDateInfo,
  getCeilingTime,
  getYearList,
  getPrimaryBtnProp,
  timeFormat12to24Converter,
  timeInFormat,
  getModifiedTimeInDate,
  renderMonthOrYearSelectorBody,
} from './CalendarHelper';
import type {
  CalendarProps,
  RefType,
  CalendarTheme,
  NavigationButtonDisable,
  CalendarMonthYearComponentPropType,
  MonthYearSelectorComponentProptype,
} from './../types/propTypes';
import styles from './Calendar.module.scss';

const cx = classNames.bind(styles);
// month and year selector
const renderMonthYearSelectorComponent = ({
  isOnlyMonthYearSelector,
  activeSelector,
  displayYear,
  displayMonth,
  navigate,
  onSelectorClick,
}: MonthYearSelectorComponentProptype) => {
  const isMonthSelector = activeSelector === SelectorType.MONTH;
  const isYearSelector = activeSelector === SelectorType.YEAR;
  const NavigationMonthClassname = cx('navigation-selector', {
    'active-selector': isMonthSelector,
    'navigation-icon-disable': isMonthSelector || isYearSelector,
    'only-selector': isOnlyMonthYearSelector,
  });
  const NavigationYearClassname = cx('navigation-selector', {
    'active-selector': isYearSelector,
    'navigation-icon-disable': isYearSelector,
    'only-selector': isOnlyMonthYearSelector,
  });
  return (
    <>
      <section className={NavigationMonthClassname} data-testid="calendar-month-selector">
        <FAIcon
          iconName="chevron-left"
          onClick={(e) => navigate(e, 'left')}
          theme={Theme.BLACK}
          className={styles['left-nav-icon']}
        />
        <span className={styles['navigate-month-text']} onClick={() => onSelectorClick(SelectorType.MONTH)}>
          {displayMonth}{' '}
          <FAIcon
            iconName="caret-down"
            iconStyle={IconStyle.SOLID}
            theme={Theme.BLACK}
            className={styles['navigation-selector-icon']}
          />
        </span>
        <FAIcon
          iconName="chevron-right"
          onClick={(e) => navigate(e, 'right')}
          theme={Theme.BLACK}
          className={styles['right-nav-icon']}
        />
      </section>
      <section className={NavigationYearClassname} data-testid="calendar-year-selector">
        <FAIcon
          iconName="chevron-left"
          onClick={() => navigate(undefined, undefined, Number(displayYear) - 1)}
          theme={Theme.BLACK}
          className={styles['left-nav-icon']}
        />
        <span className={styles['navigate-year-text']} onClick={() => onSelectorClick(SelectorType.YEAR)}>
          {displayYear}{' '}
          <FAIcon
            iconName="caret-down"
            iconStyle={IconStyle.SOLID}
            theme={Theme.BLACK}
            className={styles['navigation-selector-icon']}
          />
        </span>
        <FAIcon
          iconName="chevron-right"
          onClick={() => navigate(undefined, undefined, Number(displayYear) + 1)}
          theme={Theme.BLACK}
          className={styles['right-nav-icon']}
        />
      </section>
    </>
  );
};
// Create component as per showYearDropdown
const calendarMonthYearComponent = ({
  displayMonth,
  displayYear,
  showYearDropdown,
  yearDropdownData,
  yearDropdownConfigData,
  yearList,
  navigate,
}: CalendarMonthYearComponentPropType) => (
  <span className={styles['navigate-year-dropdown-container']}>
    {`${displayMonth} ${!showYearDropdown ? displayYear : ''}`}
    {showYearDropdown && (
      <DropDown
        className={styles['navigate-year-dropdown']}
        name="year-picker"
        dropdownLabel={displayYear}
        value={Number(displayYear)}
        dropDownType={DropDownType.INLINE}
        {...optionalProp('data', showYearDropdown === CalendarYearDropdownType.CUSTOM ? yearDropdownData : yearList)}
        configData={{
          label:
            showYearDropdown === CalendarYearDropdownType.CUSTOM ? (yearDropdownConfigData?.label as string) : 'name',
          onSelect: (item: Data) => {
            if (showYearDropdown === CalendarYearDropdownType.CUSTOM) {
              yearDropdownConfigData?.onSelect(item);
            }
            navigate(undefined, undefined, item.name as number);
          },
        }}
      />
    )}
  </span>
);
function calendarBodyMapping({
  currentPicker,
  startTimeBody,
  endTimeBody,
  calendarMainBody,
}: {
  currentPicker: ReactNode;
  startTimeBody: JSX.Element;
  endTimeBody: JSX.Element;
  calendarMainBody: JSX.Element;
}) {
  const { START_TIME, END_TIME } = TimePickerModal;
  switch (currentPicker) {
    case START_TIME:
      return startTimeBody;
    case END_TIME:
      return endTimeBody;
    default:
      return calendarMainBody;
  }
}
// Calendar Component
const Calendar = forwardRef<HTMLDivElement | RefType, CalendarProps>(
  (
    {
      showFullCalendar = true,
      showTime = false,
      onChange,
      disabledDates,
      disabledPastDates,
      timeLabel,
      timeLabelStart,
      timeLabelEnd,
      selectRange = false,
      onNavigate,
      value,
      show31DayCalendar = false,
      cancelLabel,
      scheduleLabel,
      valueTo,
      startTimeValue,
      endTimeValue,
      ariaLabel,
      calendarType = CalendarType.DEFAULT,
      calendarTitle = '',
      calendarHeading = '',
      show4WeeksOnly = false,
      type = DatePickerType.DROP_DOWN,
      is24HoursFormat = true,
      disablePastTime = false,
      disableDate,
      disableTime,
      disableMonth,
      disableYear,
      theme = Theme.ACTION,
      navigationButtonDisable = { disableLeft: false, disableRight: false },
      scheduleEventOnDates,
      onClose,
      showYearDropdown,
      yearDropdownData,
      yearDropdownConfigData,
      useReactPortal,
      portalContainerId,
      hideCalendarTitle = false,
      autoCloseMonthYearSelector = false,
    },
    ref,
  ) => {
    const t = useTranslation();

    const [calendarModalDateTime, setCalendarModalDateTime] = useState(value);
    const [calendarInterimDateTime, setCalendarInterimDateTime] = useState(value);
    const [calendarInterimEndDateTime, setCalendarInterimEndDateTime] = useState(valueTo);
    const [calendarModalEndDateTime, setCalendarModalEndDateTime] = useState(valueTo);
    // list of 100 year from 1970
    const defaultYearList = useMemo(() => getYearList(), []);
    const [startTime, setStartTime] = useState<string | undefined>(getCeilingTime(is24HoursFormat, startTimeValue));
    const [endTime, setEndTime] = useState<string | undefined>(getCeilingTime(is24HoursFormat, endTimeValue));
    const isMonthYearSelector =
      calendarType === CalendarType.MONTH_YEAR_SELECTOR || calendarType === CalendarType.ONLY_MONTH_YEAR_SELECTOR;
    const [currentPicker, setCurrentPicker] = useState<TimePickerModal | SelectorType>(
      calendarType === CalendarType.ONLY_MONTH_YEAR_SELECTOR ? SelectorType.MONTH : TimePickerModal.NONE,
    );
    const dateTimePickerRef = useRef() as MutableRefObject<HTMLDivElement>;
    useImperativeHandle(ref, () => dateTimePickerRef.current);

    const ariaLabelValue = ariaLabel || t('nextgen.Calendar.ariaLabel');
    // update value if props updated after render
    useEffect(() => {
      setCalendarModalDateTime(value);
      setCalendarInterimDateTime(value);
      if (showTime) {
        const currentStartTime = timeInFormat(is24HoursFormat, startTimeValue);
        setStartTime(currentStartTime);
      }
      if (selectRange) {
        setCalendarInterimEndDateTime(valueTo);
        setCalendarModalEndDateTime(valueTo);
        if (showTime) {
          const currentEndTime = timeInFormat(is24HoursFormat, endTimeValue);
          setEndTime(currentEndTime);
        }
      }
    }, [value, valueTo, startTimeValue, endTimeValue]);

    const { date, year, month } = getDateInfo(
      show4WeeksOnly,
      calendarInterimDateTime,
      yearDropdownConfigData?.defaultYear,
    );
    const days = t('nextgen.Calendar.days').split(',');
    const months = t('nextgen.Calendar.months').split(',');
    const timeLabelPlaceHolder = t('nextgen.Calendar.TimeLabel');
    const startTimePlaceHolder = t('nextgen.Calendar.StartTimeLabel');
    const endTimePlaceHolder = t('nextgen.Calendar.EndTimeLabel');

    const handleCalendarTimePicker = (isEndTime = false) => {
      setCurrentPicker(isEndTime ? TimePickerModal.END_TIME : TimePickerModal.START_TIME);
    };
    const navigate = (
      event?: ReactKeyboardEvent | ReactMouseEvent,
      direction?: 'left' | 'right',
      currentYear?: number,
    ) => {
      if (direction) {
        const newYear = newYearDirection(direction, year, month);
        const newMonth = newMonthDirection(direction, month);
        setCalendarInterimDateTime(new Date(newYear, newMonth));
      }
      if (currentYear) {
        setCalendarInterimDateTime(new Date(currentYear, month));
      }
      onNavigate?.(event);
    };
    const onSelectorClick = (origin: SelectorType) => {
      setCurrentPicker(origin === currentPicker ? TimePickerModal.NONE : origin);
    };
    const onDateTimeChange = (
      date?: Date,
      isMouseOverEvent = false,
      currentStartTime?: string,
      currentEndTime?: string,
      event?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
    ) => {
      const returnObj = {
        dateTime: date,
        todate: valueTo,
        startTime: currentStartTime,
        endTime: currentEndTime,
      };
      if (selectRange && value && date) {
        if (value.getTime() < date.getTime()) {
          if (!valueTo && isMouseOverEvent) {
            setCalendarInterimEndDateTime(date);
          } else if (!valueTo) {
            returnObj.dateTime = value;
            returnObj.todate = date;
          } else {
            returnObj.dateTime = date;
            returnObj.todate = undefined;
            if (!isMouseOverEvent) {
              setCalendarInterimDateTime(date);
            }
            setCalendarInterimEndDateTime(undefined);
          }
        } else {
          returnObj.dateTime = date;
          returnObj.todate = undefined;
          if (!isMouseOverEvent) {
            setCalendarInterimDateTime(date);
          }
          setCalendarInterimEndDateTime(undefined);
        }
      }
      if (returnObj.dateTime && returnObj.startTime) {
        const [hour, minute] = timeFormat12to24Converter(returnObj.startTime);
        returnObj.dateTime.setHours(hour as number);
        returnObj.dateTime.setMinutes(minute as number);
      }
      if (returnObj.todate && returnObj.endTime) {
        const [hour, minute] = timeFormat12to24Converter(returnObj.endTime);
        returnObj.todate.setHours(hour as number);
        returnObj.todate.setMinutes(minute as number);
      }

      const { modifiedDate, modifiedToDate } = getModifiedTimeInDate(
        is24HoursFormat,
        returnObj.dateTime,
        returnObj.todate,
        returnObj.startTime,
        returnObj.endTime,
      );
      if (!isMouseOverEvent) {
        onChange?.(modifiedDate, modifiedToDate, returnObj.startTime, returnObj.endTime, event);
      }
    };
    // Triggers when APPLY button click
    const handlePrimaryBtnClick = (event: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent) => {
      if (currentPicker === TimePickerModal.NONE) {
        if (calendarModalDateTime && startTime) {
          const [hour, minute] = timeFormat12to24Converter(startTime);
          calendarModalDateTime.setHours(hour as number);
          calendarModalDateTime.setMinutes(minute as number);
        }
        if (calendarModalEndDateTime && endTime) {
          const [hour, minute] = timeFormat12to24Converter(endTime);
          calendarModalEndDateTime.setHours(hour as number);
          calendarModalEndDateTime.setMinutes(minute as number);
        }
        onChange?.(calendarModalDateTime, calendarModalEndDateTime, startTime, endTime, event);
        handleCancelBtnClick();
      } else {
        setCurrentPicker(TimePickerModal.NONE);
      }
    };
    // Triggers when CLEAR button click
    const handleDateTimeClear = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      setCalendarModalDateTime(undefined);
      setCalendarModalEndDateTime(undefined);
      setCalendarInterimDateTime(undefined);
      setCalendarInterimEndDateTime(undefined);

      setStartTime(undefined);
      setEndTime(undefined);
      onChange?.(undefined, undefined, undefined, undefined, event);
      handleCancelBtnClick();
    };
    // Intermediate function, called when calendar is in modal
    const handleDateTimeUpdate = (
      event: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
      selectedDate: Date,
      isMouseOverEvent = false,
      currentStartTime?: string,
      currentEndTime?: string,
      isApplyClick?: boolean,
    ) => {
      if (type !== DatePickerType.MODAL || isApplyClick) {
        onDateTimeChange(selectedDate, isMouseOverEvent, currentStartTime, currentEndTime, event);
      } else if (selectRange) {
        // handle range in modal
        const tempDate = calendarModalDateTime || date;
        if (tempDate.getTime() < selectedDate.getTime()) {
          if (!calendarModalEndDateTime && isMouseOverEvent) {
            setCalendarInterimEndDateTime(selectedDate);
          } else if (!calendarModalEndDateTime) {
            setCalendarModalEndDateTime(selectedDate);
            setCalendarInterimEndDateTime(undefined);
          } else if (!isMouseOverEvent) {
            setCalendarModalEndDateTime(undefined);
            setCalendarInterimEndDateTime(undefined);
            setCalendarModalDateTime(selectedDate);
          }
        } else if (!isMouseOverEvent) {
          setCalendarModalDateTime(selectedDate);
          setCalendarModalEndDateTime(undefined);
          setCalendarInterimEndDateTime(undefined);
        }
      } else {
        if (!isMouseOverEvent) {
          setCalendarModalDateTime(selectedDate);
        }
      }
    };
    const handleOnStartTimeChange = (time: string) => {
      const { modifiedDate, modifiedToDate } = getModifiedTimeInDate(is24HoursFormat, value, valueTo, time, endTime);
      setStartTime(time);
      onChange?.(modifiedDate, modifiedToDate, time, endTime);
    };
    const handleOnEndTimeChange = (time: string) => {
      const { modifiedDate, modifiedToDate } = getModifiedTimeInDate(is24HoursFormat, value, valueTo, startTime, time);
      setEndTime(time);
      onChange?.(modifiedDate, modifiedToDate, startTime, time);
    };
    // Triggers when CANCEL button click
    const handleCancelBtnClick = () => {
      if (currentPicker === TimePickerModal.NONE) {
        setCalendarModalDateTime(value);
        setCalendarModalEndDateTime(valueTo);
        setCalendarInterimDateTime(value);
        setCalendarInterimEndDateTime(valueTo);
        setStartTime(getCeilingTime(is24HoursFormat, startTimeValue));
        setEndTime(getCeilingTime(is24HoursFormat, endTimeValue));
        onClose?.();
      } else {
        setStartTime(getCeilingTime(is24HoursFormat, startTimeValue));
        setEndTime(getCeilingTime(is24HoursFormat, endTimeValue));
        setCurrentPicker(TimePickerModal.NONE);
      }
    };
    // update primary button status
    const isTimePickerModalType = type === DatePickerType.MODAL;
    let dayMapPayload = {
      baseDate: value,
      baseEndDate: valueTo,
      intermediateDate: calendarInterimDateTime,
      intermediateEndDate: calendarInterimEndDateTime,
      showFullCalendar,
      show31DayCalendar,
      show4WeeksOnly,
      handleDateTimeUpdate,
      selectRange,
      scheduleEventOnDates,
      ...{ ...optionalProp('disableDate', disableDate) },
      ...{ ...optionalProp('disabledDates', disabledDates) },
      ...{ ...optionalProp('disabledPastDates', disabledPastDates) },
      ...{ ...optionalProp('defaultYear', yearDropdownConfigData?.defaultYear) },
      ariaLabel: ariaLabelValue,
    };
    if (isTimePickerModalType) {
      dayMapPayload = {
        ...dayMapPayload,
        baseDate: calendarModalDateTime,
        baseEndDate: calendarModalEndDateTime,
      };
    }

    //generate dates of current month in viewport
    const dates = useMemo(() => calendarDaysMapping(dayMapPayload), [dayMapPayload]);
    const timeList = useMemo(
      () => getTimeList(15, !is24HoursFormat, 0, disablePastTime, value),
      [is24HoursFormat, disablePastTime, value],
    );
    const { btnTitle, btnCB, isDisable } = getPrimaryBtnProp(
      value,
      calendarModalDateTime,
      valueTo,
      calendarModalEndDateTime,
      startTimeValue,
      startTime,
      endTimeValue,
      endTime,
      selectRange,
      showTime,
      handlePrimaryBtnClick,
      handleDateTimeClear,
      currentPicker,
      scheduleLabel,
    );

    const CClassname = cx(
      'calendar-container',
      {
        'show-calendar-title-only': Boolean(calendarTitle),
        'month-year-selector': isMonthYearSelector,
      },
      [theme],
    );
    const DateClassname = cx({ 'date-row-range': selectRange }, 'day-sequence');
    const TimeClassname = cx('time-container', { 'modal-time-container': isTimePickerModalType });
    const HeaderClassname = cx('month-container', {
      [`active-selector-${currentPicker}`]: isMonthYearSelector,
    });
    // ui components
    const modalHeader = (
      <div className={styles['calendar-modal-header']}>
        <section
          className={`${modalStyles['modal-headline']} ${modalStyles[`modal-headline-${TextAlign.CENTER}`]}`}
          data-testid="modal-healine-test-id"
        >
          {calendarHeading}
        </section>
        <div className={modalStyles['modal-icon-section']}>
          <FAIcon
            className={styles['icon-close']}
            data-testid="close-icon-test-id"
            iconName="xmark"
            theme={Theme.BLACK}
            onClick={() => {
              setCurrentPicker(TimePickerModal.NONE);
              onClose?.();
            }}
          />
        </div>
      </div>
    );
    const modalFooter = (
      <>
        <Button
          title={btnTitle}
          type={ButtonTypes.BUTTON}
          theme={Theme.BLACK}
          size={ButtonSize.SMALL}
          onClick={btnCB}
          disabled={isDisable}
          data-testid="primary-button-test-id"
        />
        <Button
          title={cancelLabel ?? 'Cancel'}
          type={ButtonTypes.BUTTON}
          theme={Theme.BLACK}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          onClick={handleCancelBtnClick}
          data-testid="secondary-button-test-id"
        />
      </>
    );
    const handleDisableMonthCB = (month: string) => disableMonth?.(month, year) ?? false;

    const startTimeLabel = selectedRangeLabel(selectRange, timeLabelStart, timeLabel || timeLabelPlaceHolder);
    // Renders calendar header with title, month/year selector, or navigation controls
    const renderCalendarHeader = () => {
      if (hideCalendarTitle) {
        return null;
      }
      let headerBody: JSX.Element | null = null;
      if (calendarTitle) {
        headerBody = (
          <span className={styles['calendar-title']} data-testid="calendar-title-testid">
            {calendarTitle}
          </span>
        );
      } else if (isMonthYearSelector) {
        headerBody = renderMonthYearSelectorComponent({
          isOnlyMonthYearSelector: calendarType === CalendarType.ONLY_MONTH_YEAR_SELECTOR,
          activeSelector: currentPicker as SelectorType,
          displayMonth: months[month] as string,
          displayYear: `${year}`,
          navigate,
          onSelectorClick,
        });
      } else {
        headerBody = (
          <>
            <span className={styles['navigation-month-year']}>
              {calendarMonthYearComponent({
                displayMonth: months[month] as string,
                displayYear: `${year}`,
                yearList: defaultYearList,
                navigate,
                ...{ ...optionalProp('showYearDropdown', showYearDropdown) },
                ...{ ...optionalProp('yearDropdownData', yearDropdownData) },
                ...{ ...optionalProp('yearDropdownConfigData', yearDropdownConfigData) },
              })}
            </span>

            <span className={styles['navigation-container']}>
              <FAIcon
                iconName="arrow-left"
                className={`${styles['navigation-left']} ${
                  navigationButtonDisable.disableLeft ? styles['disabled-nav-btn'] : ''
                }`}
                data-testid="navigation-left"
                onClick={(e) => navigate(e, 'left')}
              />

              <FAIcon
                iconName="arrow-right"
                className={`${styles['navigation-right']} ${
                  navigationButtonDisable.disableRight ? styles['disabled-nav-btn'] : ''
                }`}
                data-testid="navigation-right"
                onClick={(e) => navigate(e, 'right')}
              />
            </span>
          </>
        );
      }
      return <div className={HeaderClassname}>{headerBody}</div>;
    };
    // Renders month/year selector or calendar dates with time pickers
    const renderMonthAndYearPicker = () => {
      if (currentPicker === SelectorType.MONTH || currentPicker === SelectorType.YEAR) {
        return renderMonthOrYearSelectorBody(
          setCalendarInterimDateTime,
          currentPicker,
          months,
          month,
          yearDropdownData || defaultYearList,
          year,
          onSelectorClick,
          autoCloseMonthYearSelector,
          calendarType === CalendarType.ONLY_MONTH_YEAR_SELECTOR ? handleDateTimeUpdate : undefined,
          handleDisableMonthCB,
          disableYear,
        );
      }
      return (
        <>
          <div className={styles['dates-container']}>
            {showFullCalendar && (
              <div className={DateClassname}>
                {days.map((day) => (
                  <span className={`${styles['day-cell']} ${styles['heading-cell']}`} key={day}>
                    {day}
                  </span>
                ))}
              </div>
            )}
            {dates}
          </div>
          {showFullCalendar && showTime && (
            <div className={TimeClassname}>
              {startTimeLabel ? <FAIcon iconName="clock" className={styles['time-icon']} /> : ''}
              <span className={styles['time-label']} data-testid="time-label">
                {startTimeLabel}
              </span>
              <span className={styles['time-dropdown']}>
                {isTimePickerModalType ? (
                  <>
                    <button
                      className={styles['time-value-label']}
                      onClick={() => handleCalendarTimePicker()}
                      onKeyDown={() => handleCalendarTimePicker()}
                      aria-label={startTimePlaceHolder}
                      tabIndex={0}
                      data-testid="start-time-element"
                    >
                      {startTime || startTimePlaceHolder}
                    </button>
                    <div className={styles['time-value-divider']}></div>
                  </>
                ) : (
                  <DropDown
                    className={`${styles['dropdown-box']} ${styles['base-dropdown-result']} ${styles['open-dropdown-result']}`}
                    name="time-picker"
                    data={timeList}
                    configData={{
                      label: 'name',
                      onSelect: (item: Data) => handleOnStartTimeChange(item.name as string),
                      disableItemKey: 'isDisabled',
                    }}
                    value={startTime || startTimeValue}
                    mlpVersion={MLPVersion.ONE}
                  />
                )}
              </span>
            </div>
          )}
          {showFullCalendar && showTime && selectRange && (
            <div className={TimeClassname}>
              {timeLabelEnd ? <FAIcon iconName={'clock'} className={styles['time-icon']} /> : ''}
              <span className={styles['time-label']} data-testid="time-label">
                {timeLabelEnd ?? endTimePlaceHolder}
              </span>
              <span className={styles['time-dropdown']}>
                {isTimePickerModalType ? (
                  <>
                    <button
                      className={styles['time-value-label']}
                      onClick={() => handleCalendarTimePicker(true)}
                      onKeyDown={() => handleCalendarTimePicker(true)}
                      aria-label={endTimePlaceHolder}
                      tabIndex={0}
                      data-testid="end-time-element"
                    >
                      {endTime || endTimePlaceHolder}
                    </button>
                    <div className={styles['time-value-divider']}></div>
                  </>
                ) : (
                  <DropDown
                    className={`${styles['dropdown-box']} ${styles['base-dropdown-result']} ${styles['open-dropdown-result']}`}
                    name="time-picker"
                    data={timeList}
                    configData={{
                      label: 'name',
                      onSelect: (item: Data) => handleOnEndTimeChange(item.name as string),
                    }}
                    value={endTime || endTimeValue}
                    mlpVersion={MLPVersion.ONE}
                  />
                )}
              </span>
            </div>
          )}
        </>
      );
    };
    const calendarMainBody = (
      <div className={CClassname} data-testid="calendar-container" ref={dateTimePickerRef} aria-label={ariaLabelValue}>
        {renderCalendarHeader()}
        {renderMonthAndYearPicker()}
      </div>
    );
    const startTimeBody = (
      <TimePicker
        disablePastTime={disablePastTime}
        title={startTimePlaceHolder}
        onTimeChange={setStartTime}
        {...optionalProp('value', startTime)}
        is24HoursFormat={is24HoursFormat}
        disableTime={(time: string) => disableTime?.(time, calendarModalDateTime || date) || false}
      />
    );
    const endTimeBody = (
      <TimePicker
        disablePastTime={disablePastTime}
        title={endTimePlaceHolder}
        onTimeChange={setEndTime}
        {...optionalProp('value', endTime)}
        disableTime={(time: string) => disableTime?.(time, calendarModalEndDateTime || date) || false}
        is24HoursFormat={is24HoursFormat}
      />
    );
    if (isTimePickerModalType) {
      return (
        <RawModal
          id="calendar_modal"
          header={modalHeader}
          footer={modalFooter}
          {...optionalProp('onClose', onClose)}
          {...optionalProp('useReactPortal', useReactPortal)}
          {...optionalProp('portalContainerId', portalContainerId)}
          textAlign={TextAlign.LEFT}
        >
          {calendarBodyMapping({
            currentPicker,
            startTimeBody,
            endTimeBody,
            calendarMainBody,
          })}
        </RawModal>
      );
    }
    return calendarBodyMapping({
      currentPicker,
      startTimeBody,
      endTimeBody,
      calendarMainBody,
    });
  },
);
Calendar.displayName = 'Calendar';
export type { CalendarTheme, NavigationButtonDisable };
export { CalendarYearDropdownType, CalendarType };
export default Calendar;
